/**
 * pages/RFPBuilderV3.jsx
 * Full RFP builder with 5 sections and per-line margin.
 *
 * Sections:
 *   1. OCI Cloud Services    (oci_recurring)
 *   2. Third-party / Custom  (third_party)
 *   3. Implementation        (implementation)
 *   4. BSPL Managed Services (managed_service) — L1/L2/L3
 *   5. Annual Support / AMC  (annual_support)
 *
 * Margin:
 *   - Per line margin_pct field
 *   - Admin users see: Cost price | Margin % | Sell price
 *   - Readonly/client users see: Cost only — margin hidden
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import * as api from "../api/client";
import {
  Card, Btn, SectionTitle, Badge, ErrorBanner,
  SuccessBanner, Spinner, Empty,
} from "../components/UI";

const fmtINR = n => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt    = (n, d=0) => n == null ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

// ─── Section config ───────────────────────────────────────────────────────────
const SECTIONS = [
  { id: "oci_recurring",   label: "OCI cloud services",         icon: "☁",  color: "#2563EB", bg: "#EFF6FF",  freq: "monthly",  canHaveMargin: true },
  { id: "third_party",     label: "Third-party & custom",       icon: "⚙",  color: "#D97706", bg: "#FFFBEB",  freq: "monthly",  canHaveMargin: true },
  { id: "implementation",  label: "Implementation costs",       icon: "⚡", color: "#0F6E56", bg: "#F0FDF4",  freq: "one_time", canHaveMargin: true },
  { id: "managed_service", label: "BSPL managed services",      icon: "🛡", color: "#7C3AED", bg: "#F5F3FF",  freq: "monthly",  canHaveMargin: false },
  { id: "annual_support",  label: "Annual support / AMC",       icon: "📋", color: "#0369A1", bg: "#F0F9FF",  freq: "annual",   canHaveMargin: true },
];

const BILLING_LABELS = { monthly: "Monthly", annual: "Annual", one_time: "One-time" };

const blankLine = (catId, catalogue) => {
  const first = catalogue?.[0];
  const base = {
    line_category: catId, billing_frequency: "monthly",
    service_id: null, service_name: "", part_qty: 1, instance_qty: 1, usage_qty: 1, planned_qty: 1,
    rfp_unit_price: 0, free_tier: 0, margin_pct: 0,
    is_custom: false, custom_vendor: null,
    display_name: null, qty_description: null,
    environment: null, notes: null, sort_order: 0,
  };
  if (catId === "oci_recurring" && first) {
    return { ...base, service_id: first.service_id,
      service_name: `${first.service} – ${first.name}`,
      rfp_unit_price: first.price, free_tier: first.free_tier || 0 };
  }
  if (catId === "third_party") return { ...base, is_custom: true, display_name: "New third-party service", qty_description: "per month" };
  if (catId === "implementation") return { ...base, is_custom: true, billing_frequency: "one_time", display_name: "New implementation item", qty_description: "lump sum" };
  if (catId === "managed_service") return { ...base, is_custom: true, display_name: "L1/L2/L3 Managed support", qty_description: "per month", margin_pct: 0 };
  if (catId === "annual_support")  return { ...base, is_custom: true, billing_frequency: "annual", display_name: "Annual support / AMC", qty_description: "per year" };
  return base;
};

// ─── Derived cost calculations ────────────────────────────────────────────────
const calcLine = (r) => {
  const part_qty    = r.part_qty    || r.planned_qty || 0;
  const instance_qty = r.instance_qty || 1;
  const usage_qty   = r.usage_qty   || 1;
  const total_qty   = part_qty * instance_qty;
  const billable    = Math.max(0, total_qty - (r.free_tier || 0));
  const isBudget    = (r.planned_qty <= 1) && (r.rfp_unit_price > 1000);
  const cost_price  = isBudget
    ? billable * (r.rfp_unit_price || 0)
    : billable * (r.rfp_unit_price || 0) * usage_qty;
  const margin_amt = cost_price * ((r.margin_pct || 0) / 100);
  const sell_price = cost_price + margin_amt;
  return { billable, cost_price, margin_amt, sell_price };
};

// ─── Section panel ────────────────────────────────────────────────────────────
function SectionPanel({ sec, rows, catalogue, onUpdate, onAdd, onRemove, isAdmin }) {
  const sectionRows = rows.filter(r => r.line_category === sec.id);
  const totals = sectionRows.reduce((acc, r) => {
    const { cost_price, margin_amt, sell_price } = calcLine(r);
    return { cost: acc.cost + cost_price, margin: acc.margin + margin_amt, sell: acc.sell + sell_price };
  }, { cost: 0, margin: 0, sell: 0 });

  const serviceGroups = [...new Set((catalogue || []).map(c => c.service_group))].sort();

  return (
    <Card style={{ marginBottom: 18, borderTop: `3px solid ${sec.color}` }}>
      {/* Section header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>{sec.icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>{sec.label}</div>
            <div style={{ fontSize: 12, color: "#9CA3AF" }}>{BILLING_LABELS[sec.freq]} · {sectionRows.length} line{sectionRows.length !== 1 ? "s" : ""}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          {isAdmin && sec.canHaveMargin && totals.margin > 0 && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, color: "#9CA3AF", textTransform: "uppercase", fontWeight: 600 }}>Margin</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#16A34A" }}>{fmtINR(totals.margin)}</div>
            </div>
          )}
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, color: "#9CA3AF", textTransform: "uppercase", fontWeight: 600 }}>
              {sec.freq === "annual" ? "Annual total" : sec.freq === "one_time" ? "One-time total" : "Monthly total"}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: sec.color }}>
              {fmtINR(isAdmin ? totals.sell : totals.cost_price)}
            </div>
            {isAdmin && totals.margin > 0 && (
              <div style={{ fontSize: 11, color: "#9CA3AF" }}>cost: {fmtINR(totals.cost)}</div>
            )}
          </div>
          <Btn onClick={() => onAdd(sec.id)} size="sm" variant="secondary">+ Add line</Btn>
        </div>
      </div>

      {sectionRows.length === 0
        ? <Empty icon={sec.icon} title={`No ${sec.label.toLowerCase()} lines`} sub="Click '+ Add line' to start" />
        : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: sec.bg, borderBottom: `2px solid ${sec.color}22` }}>
                  <th style={th}>Service / Item</th>
                  {sec.id === "third_party" && <th style={th}>Vendor</th>}
                  {sec.id !== "oci_recurring" && <th style={th}>Unit / Billing</th>}
                  {sec.id === "oci_recurring" && <th style={{...th, textAlign:"right"}}>Free tier</th>}
                  {sec.id === "oci_recurring" && <th style={{...th, textAlign:"right"}}>Part Qty</th>}
                  {sec.id === "oci_recurring" && <th style={{...th, textAlign:"right"}}>Instances</th>}
                  {sec.id === "oci_recurring" && <th style={{...th, textAlign:"right"}}>Usage Qty</th>}
                  {sec.id !== "oci_recurring" && <th style={{...th, textAlign:"right"}}>Qty</th>}
                  <th style={{...th, textAlign:"right"}}>Unit price (₹)</th>
                  <th style={{...th, textAlign:"right"}}>Cost (₹)</th>
                  {isAdmin && sec.canHaveMargin && <th style={{...th, textAlign:"right"}}>Margin %</th>}
                  {isAdmin && sec.canHaveMargin && <th style={{...th, textAlign:"right"}}>Sell price (₹)</th>}
                  <th style={th}>Env / Notes</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {sectionRows.map((row) => {
                  const globalIdx = rows.indexOf(row);
                  const { cost_price, margin_amt, sell_price } = calcLine(row);
                  const rowBg = rows.indexOf(row) % 2 === 0 ? "#fff" : "#FAFAFA";

                  return (
                    <tr key={globalIdx} style={{ background: rowBg, borderBottom: "1px solid #F3F4F6" }}>
                      {/* Service name / selector */}
                      <td style={td}>
                        {sec.id === "oci_recurring" ? (
                          <div>
                            <select
                              value={row.service_id || ""}
                              onChange={e => {
                                const cat = (catalogue||[]).find(c => c.service_id === e.target.value);
                                if (cat) onUpdate(globalIdx, {
                                  service_id: cat.service_id,
                                  service_name: `${cat.service} – ${cat.name}`,
                                  rfp_unit_price: cat.price,
                                  free_tier: cat.free_tier || 0,
                                });
                              }}
                              style={{ ...sel, minWidth: 200 }}
                            >
                              {serviceGroups.map(g => (
                                <optgroup key={g} label={g}>
                                  {(catalogue||[]).filter(c=>c.service_group===g).map(c=>(
                                    <option key={c.service_id} value={c.service_id}>{c.service} — {c.name} ({c.unit})</option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <input value={row.display_name || ""} onChange={e => onUpdate(globalIdx, { display_name: e.target.value })}
                            placeholder={sec.id === "managed_service" ? "e.g. L1/L2 support bundle" : "Item description"}
                            style={{ ...inp, minWidth: 200 }} />
                        )}
                      </td>

                      {/* Vendor (third_party only) */}
                      {sec.id === "third_party" && (
                        <td style={td}>
                          <input value={row.custom_vendor||""} onChange={e=>onUpdate(globalIdx,{custom_vendor:e.target.value})}
                            placeholder="Vendor name" style={{...inp, width:100}}/>
                        </td>
                      )}

                      {/* Unit / Billing (non-OCI) */}
                      {sec.id !== "oci_recurring" && (
                        <td style={td}>
                          <input value={row.qty_description||""} onChange={e=>onUpdate(globalIdx,{qty_description:e.target.value})}
                            placeholder="per month / person-day" style={{...inp, width:120}}/>
                        </td>
                      )}

                      {/* Free tier (OCI only) */}
                      {sec.id === "oci_recurring" && (
                        <td style={{...td, textAlign:"right", color:"#16A34A", fontSize:12}}>
                          {row.free_tier > 0 ? fmt(row.free_tier) : "—"}
                        </td>
                      )}

                      {/* Qty */}
                      <td style={td}>
                        {sec.id === "oci_recurring" ? (<>
                          <input type="number" value={row.part_qty||1}
                            onChange={e=>onUpdate(globalIdx,{part_qty:Number(e.target.value)})}
                            style={{...inp, width:65, textAlign:"right"}}
                            title="Part Qty — size per unit (OCPUs, GB)"/>
                        </>) : (
                          <input type="number" value={row.planned_qty||0}
                            onChange={e=>onUpdate(globalIdx,{planned_qty:Number(e.target.value)})}
                            style={{...inp, width:80, textAlign:"right"}}/>
                        )}
                      </td>
                      {sec.id === "oci_recurring" && (
                        <td style={td}>
                          <input type="number" value={row.instance_qty||1}
                            onChange={e=>onUpdate(globalIdx,{instance_qty:Number(e.target.value)})}
                            style={{...inp, width:65, textAlign:"right"}}
                            title="Instance Qty — number of VMs/instances"/>
                        </td>
                      )}
                      {sec.id === "oci_recurring" && (
                        <td style={td}>
                          <input type="number" value={row.usage_qty||1}
                            onChange={e=>onUpdate(globalIdx,{usage_qty:Number(e.target.value)})}
                            style={{...inp, width:65, textAlign:"right"}}
                            title="Usage Qty — hours/month (744) or 1 for monthly"/>
                        </td>
                      )}

                      {/* Unit price */}
                      <td style={td}>
                        <input type="number" step="0.0001" value={row.rfp_unit_price||0} onChange={e=>onUpdate(globalIdx,{rfp_unit_price:Number(e.target.value)})}
                          style={{...inp, width:100, textAlign:"right"}}/>
                      </td>

                      {/* Cost price */}
                      <td style={{...td, textAlign:"right", fontWeight:600, color:"#374151"}}>
                        {fmtINR(cost_price)}
                      </td>

                      {/* Margin % (admin only) */}
                      {isAdmin && sec.canHaveMargin && (
                        <td style={td}>
                          <div style={{display:"flex",alignItems:"center",gap:4}}>
                            <input type="number" min="0" max="100" step="0.5"
                              value={row.margin_pct||0}
                              onChange={e=>onUpdate(globalIdx,{margin_pct:Number(e.target.value)})}
                              style={{...inp, width:60, textAlign:"right",
                                background: row.margin_pct > 0 ? "#F0FDF4" : "#fff",
                                borderColor: row.margin_pct > 0 ? "#86EFAC" : "#E5E7EB"}}/>
                            <span style={{fontSize:12,color:"#6B7280"}}>%</span>
                          </div>
                        </td>
                      )}

                      {/* Sell price (admin only) */}
                      {isAdmin && sec.canHaveMargin && (
                        <td style={{...td, textAlign:"right", fontWeight:700, color: sell_price > cost_price ? "#16A34A" : "#374151"}}>
                          {fmtINR(sell_price)}
                          {margin_amt > 0 && <div style={{fontSize:10, color:"#22C55E"}}>+{fmtINR(margin_amt)}</div>}
                        </td>
                      )}

                      {/* Env / Notes */}
                      <td style={td}>
                        <input value={row.environment||""} onChange={e=>onUpdate(globalIdx,{environment:e.target.value})}
                          placeholder="Prod/DR/Dev" style={{...inp,width:70}}/>
                      </td>

                      {/* Delete */}
                      <td style={td}>
                        <button onClick={()=>onRemove(globalIdx)} style={delBtn}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      }
    </Card>
  );
}

const th  = { padding:"8px 12px", textAlign:"left", color:"#6B7280", fontWeight:600, fontSize:12, whiteSpace:"nowrap" };
const td  = { padding:"7px 10px", whiteSpace:"nowrap" };
const sel = { padding:"4px 8px", border:"1px solid #E5E7EB", borderRadius:6, fontSize:12, background:"#fff", cursor:"pointer" };
const inp = { padding:"4px 8px", border:"1px solid #E5E7EB", borderRadius:6, fontSize:13, fontFamily:"'DM Sans',sans-serif" };
const delBtn = { background:"#FEF2F2", color:"#DC2626", border:"none", borderRadius:6, padding:"3px 8px", cursor:"pointer", fontSize:12 };

// ─── Main component ───────────────────────────────────────────────────────────
export default function RFPBuilderV3({ clientId, clientName }) {
  const { isAdmin, isReadonly } = useAuth();
  const [lines, setLines]       = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState("");
  const [localLines, setLocalLines] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([
        api.rfp.list(clientId),
        api.catalogue.list(),
      ]);
      setLines(l); setCatalogue(c);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const rows = localLines ?? lines ?? [];

  const updateRow = useCallback((idx, changes) => {
    if (isReadonly) return;
    setLocalLines(prev => {
      const base = prev ?? lines ?? [];
      return base.map((r, i) => i === idx ? { ...r, ...changes } : r);
    });
  }, [lines, isReadonly]);

  const addRow = useCallback((catId) => {
    if (isReadonly) return;
    setLocalLines(prev => {
      const base = prev ?? lines ?? [];
      return [...base, blankLine(catId, catalogue)];
    });
  }, [lines, catalogue, isReadonly]);

  const removeRow = useCallback((idx) => {
    if (isReadonly) return;
    setLocalLines(prev => (prev ?? lines ?? []).filter((_, i) => i !== idx));
  }, [lines, isReadonly]);

  const save = async () => {
    if (isReadonly) return;
    setSaving(true); setError("");
    try {
      const toSave = rows.map((r, i) => ({
        ...r, sort_order: i,
        service_id:   r.service_id   || null,
        service_name: r.service_name || r.display_name || "",
        display_name: r.display_name || null,
      }));
      await api.rfp.replace(clientId, toSave);
      setSaved(true); setLocalLines(null);
      await load();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // ── Grand totals ───────────────────────────────────────────────────────────
  const grandTotals = SECTIONS.map(sec => {
    const secRows = rows.filter(r => r.line_category === sec.id);
    const t = secRows.reduce((acc, r) => {
      const { cost_price, margin_amt, sell_price } = calcLine(r);
      return { cost: acc.cost + cost_price, margin: acc.margin + margin_amt, sell: acc.sell + sell_price };
    }, { cost: 0, margin: 0, sell: 0 });
    return { ...sec, ...t, count: secRows.length };
  });

  const grand = grandTotals.reduce((acc, s) => ({
    cost:   acc.cost   + s.cost,
    margin: acc.margin + s.margin,
    sell:   acc.sell   + s.sell,
  }), { cost: 0, margin: 0, sell: 0 });

  if (loading) return <div style={{ textAlign:"center", padding:48 }}><Spinner size={28}/></div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <SectionTitle
          children="RFP / Budget builder"
          sub={isReadonly
            ? "View-only — contact your administrator to make changes"
            : `Build complete RFP with OCI services, third-party, implementation and support costs${isAdmin ? " · Margin visible to admins only" : ""}`}
        />
        {!isReadonly && (
          <div style={{ display:"flex", gap:10 }}>
            <Btn onClick={() => { setLocalLines(null); load(); }} variant="secondary" size="sm">↺ Reset</Btn>
            <Btn onClick={save} loading={saving}>Save RFP</Btn>
          </div>
        )}
      </div>

      {error  && <ErrorBanner message={error} onDismiss={() => setError("")} />}
      {saved  && <SuccessBanner message="RFP saved successfully" />}
      {isReadonly && (
        <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:8, padding:"8px 14px", marginBottom:16, fontSize:13, color:"#1E40AF" }}>
          👁 <strong>Read-only access</strong> — You can view the RFP but cannot make changes.
        </div>
      )}

      {/* Grand total summary */}
      <div style={{ display:"grid", gridTemplateColumns:`repeat(${isAdmin ? 6 : 5},1fr)`, gap:12, marginBottom:24 }}>
        {grandTotals.map(s => (
          <div key={s.id} style={{ background:s.bg, borderRadius:10, padding:"10px 14px", border:`1px solid ${s.color}22` }}>
            <div style={{ fontSize:10, color:s.color, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:4 }}>
              {s.icon} {s.label.split(" ")[0]}
            </div>
            <div style={{ fontSize:17, fontWeight:800, color:s.color }}>
              {fmtINR(isAdmin ? s.sell : s.cost)}
            </div>
            {isAdmin && s.margin > 0 && (
              <div style={{ fontSize:10, color:"#16A34A" }}>+{fmtINR(s.margin)} margin</div>
            )}
          </div>
        ))}
        <div style={{ background: isAdmin ? "#F0FDF4" : "#F9FAFB", borderRadius:10, padding:"10px 14px", border:`1px solid #00000011` }}>
          <div style={{ fontSize:10, color:"#111827", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:4 }}>Total monthly</div>
          <div style={{ fontSize:17, fontWeight:800, color:"#111827" }}>
            {fmtINR(isAdmin ? grand.sell : grand.cost)}
          </div>
          {isAdmin && grand.margin > 0 && (
            <div style={{ fontSize:10, color:"#16A34A" }}>
              +{fmtINR(grand.margin)} ({((grand.margin/grand.cost)*100).toFixed(1)}%)
            </div>
          )}
        </div>
      </div>

      {/* Sections */}
      {SECTIONS.map(sec => (
        <SectionPanel
          key={sec.id}
          sec={sec}
          rows={rows}
          catalogue={catalogue}
          onUpdate={updateRow}
          onAdd={addRow}
          onRemove={removeRow}
          isAdmin={isAdmin}
        />
      ))}

      {/* Bottom save */}
      {!isReadonly && (
        <div style={{ display:"flex", justifyContent:"flex-end", paddingTop:8 }}>
          <Btn onClick={save} loading={saving}>Save entire RFP</Btn>
        </div>
      )}
    </div>
  );
}
